import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Car, Settings, ParkingCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { parkingConfigurationApi } from '../services/api'
import {
  PageHeader,
  EmptyState,
  Button,
  LoadingSpinner,
} from '../components/common'
import ParkingSpots from './ParkingSpots'
import ParkingConfiguration from './ParkingConfiguration'
import ParkingOccupancy from './ParkingOccupancy'

const TABS = [
  { id: 'spots', label: 'Parking Spots', icon: Car },
  { id: 'configuration', label: 'Configuration', icon: Settings },
  { id: 'occupancy', label: 'Occupancy', icon: ParkingCircle },
]

const Parking = () => {
  const { selectedBuildingId } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('spots')
  const [setupChecked, setSetupChecked] = useState(false)
  const [isSetupCompleted, setIsSetupCompleted] = useState(false)

  useEffect(() => {
    if (!selectedBuildingId) return
    let cancelled = false
    setSetupChecked(false)
    parkingConfigurationApi
      .get(selectedBuildingId)
      .then((response) => {
        if (cancelled) return
        const data = response.data?.data || response.data
        setIsSetupCompleted(!!(data && data.parkingConfigurationId && data.isSetupCompleted))
      })
      .catch(() => {
        if (cancelled) return
        setIsSetupCompleted(false)
      })
      .finally(() => {
        if (cancelled) return
        setSetupChecked(true)
      })
    return () => {
      cancelled = true
    }
  }, [selectedBuildingId])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Parking Management"
        subtitle="Manage parking spots, configuration, and occupancy"
      />

      {!selectedBuildingId ? (
        <EmptyState
          icon={Building2}
          title="Select a building"
          description="Choose a building from the top bar to manage its parking."
        />
      ) : !setupChecked ? (
        <LoadingSpinner />
      ) : !isSetupCompleted ? (
        <EmptyState
          icon={Settings}
          title="Set Up Visitor Parking"
          description="Configure how visitors park here, how residents reserve, and any rules. Takes a couple of minutes."
          action={
            <Button variant="primary" onClick={() => navigate('/parking/setup')}>
              Set Up Parking
            </Button>
          }
        />
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'spots' && <ParkingSpots buildingId={selectedBuildingId} />}
          {activeTab === 'configuration' && <ParkingConfiguration buildingId={selectedBuildingId} />}
          {activeTab === 'occupancy' && <ParkingOccupancy buildingId={selectedBuildingId} />}
        </>
      )}
    </div>
  )
}

export default Parking
